// The cutaway panel's planes: the six directions it offers and the edits it
// makes to the view state's cutaway (docs/contracts/view-state.md, field `cut`).
// A plane {normal, offset} cuts away the points p with normal · p > offset, and
// a point beyond every plane is cut away (ADR 0011). Normals are in the engine
// frame (ADR 0008): +X the vehicle's left, +Y up, +Z forward.

import { MAX_CUT_PLANES, type CutPlane, type Vec3 } from "../../../state/view-state";

export type Side = "top" | "bottom" | "left" | "right" | "front" | "rear";

/** The directions the panel offers, in the order of its menu: each names the side cut away. */
export const SIDES: readonly { side: Side; normal: Vec3 }[] = [
  { side: "top", normal: [0, 1, 0] },
  { side: "bottom", normal: [0, -1, 0] },
  { side: "left", normal: [1, 0, 0] },
  { side: "right", normal: [-1, 0, 0] },
  { side: "front", normal: [0, 0, 1] },
  { side: "rear", normal: [0, 0, -1] },
];

/** The offset slider's range and step, in metres: wide enough for the CT (about 0.8 m across). */
export const OFFSET_MIN = -0.5;
export const OFFSET_MAX = 0.5;
export const OFFSET_STEP = 0.001;

/** The side a plane's normal points to, or null for any other direction. */
export function sideOf(plane: CutPlane): Side | null {
  const n = plane.normal;
  const len = Math.hypot(n[0], n[1], n[2]);
  for (const s of SIDES) {
    const dot = (s.normal[0] * n[0] + s.normal[1] * n[1] + s.normal[2] * n[2]) / len;
    if (dot > 1 - 1e-9) return s.side;
  }
  return null;
}

export function normalOf(side: Side): Vec3 {
  return [...SIDES.find((s) => s.side === side)!.normal];
}

/** The planes with a new one added: through the origin, facing the first side not used yet. */
export function addPlane(planes: readonly CutPlane[]): CutPlane[] {
  if (planes.length >= MAX_CUT_PLANES) return [...planes];
  const used = new Set(planes.map(sideOf));
  const side = SIDES.find((s) => !used.has(s.side)) ?? SIDES[0]!;
  return [...planes, { normal: [...side.normal], offset: 0 }];
}

export function removePlane(planes: readonly CutPlane[], index: number): CutPlane[] {
  return planes.filter((_, i) => i !== index);
}

/**
 * Turns plane `index` to face `side`. A unit normal keeps its offset; the offset
 * of a longer custom normal is rescaled, so the plane keeps its distance from the origin.
 */
export function setSide(planes: readonly CutPlane[], index: number, side: Side): CutPlane[] {
  return planes.map((p, i) => {
    if (i !== index) return p;
    const len = Math.hypot(p.normal[0], p.normal[1], p.normal[2]) || 1;
    return { normal: normalOf(side), offset: roundOffset(p.offset / len) };
  });
}

export function setOffset(planes: readonly CutPlane[], index: number, offset: number): CutPlane[] {
  return planes.map((p, i) => (i === index ? { normal: [...p.normal], offset: roundOffset(offset) } : p));
}

/** Offsets are kept to 0.1 mm, so addresses stay short. */
export function roundOffset(offset: number): number {
  return Math.round(offset * 1e4) / 1e4;
}
